import React, {useMemo, useState} from 'react'
import {Link, useSearchParams} from "react-router-dom";
import {Button, Card, Divider, Modal, Typography} from 'antd';
import {
    BankOutlined,
    BellOutlined,
    CheckCircleOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    LinkOutlined,
    QuestionCircleOutlined,
    UserOutlined
} from '@ant-design/icons';
import {useAuth} from "../../providers/AuthProvider";

const {Title, Text, Paragraph} = Typography;

const TalentVersionUpdate2025 = () => {

    const {auth, shouldSeeTalentAppUpdate} = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const requirements = useMemo(() => {

        const talentRequirements = auth.isTalentSupplierResource() ? [] : [
            {
                icon: <UserOutlined style={{fontSize: 22}} className="text-info"/>,
                title: "References",
                description: "Two references that we can contact (name, email and LinkedIn URL)",
                total: 2,
                completed: auth?.talent_references?.length || 0,
                type: 'progress'
            },
        ]
        return [
            ...talentRequirements,
            {
                icon: <BankOutlined style={{fontSize: 22}} className="text-secondary"/>,
                title: "Brand or Company Experience",
                description: "List at least one brand (or company) that you have worked with",
                total: 1,
                completed: auth?.brands?.length || 0,
                type: 'progress'
            },
            {
                icon: <FileTextOutlined style={{fontSize: 22}} className="text-success"/>,
                title: "Case Studies",
                description: "Provide at least two project highlights (headline, description)",
                total: 2,
                completed: auth?.key_clients?.length || 0,
                type: 'progress'
            },
            {
                icon: <LinkOutlined style={{fontSize: 22}} className="text-warning"/>,
                title: "Portfolio Link",
                description: "A link to your portfolio (required only If you are signed up for a creative role)",
                type: auth?.shouldRequirePortfolioLink() ? 'progress' : 'not-required',
                total: 1,
                completed: (!auth?.shouldRequirePortfolioLink() || !!auth.portfolio_url) ? 1 : 0
            },
            {
                icon: <FileSearchOutlined style={{fontSize: 22}} className="text-danger"/>,
                title: "Worker Classification Selection",
                description: "You will need to specify that you are interested in at least one type of assignment (W2-Staffing and/or 1099-Independent Contractor)",
                total: 1,
                completed: (auth?.tax_class_settings?.has_w2_classification || auth?.tax_class_settings?.has_1099_classification) ? 1 : 0,
                type: 'progress'
            }
        ]
    }, [auth]);

    const [shouldShowVersionUpdate, setShouldShowVersionUpdate] = useState(() => {
        return auth && auth.isTalent()
            && auth.isOnTalentMarketplace()
            && !auth.isTalentSupplierResource()
            // && shouldSeeTalentAppUpdate()
            && auth.isProfileApproved()
            && TB.find(requirements, req => req.completed < req.total)
    });


    const StatusIndicator = ({item}) => {
        if (item.type === 'not-required') {
            return (
                <Text type="secondary" italic className="text-sm ml-auto">
                    Not Required
                </Text>
            );
        }

        const percentage = (Math.min(item.completed, item.total) / item.total) * 100;
        const getColor = () => {
            if (percentage === 0) return TB.config('color.danger');
            if (percentage === 100) return TB.config('color.success');
            return TB.config('color.warning');
        };

        return (
            <div className="ml-auto"
                 style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px'}}>
                <Text strong style={{fontSize: '14px', color: getColor()}}>
                    {Math.min(item.completed, item.total)}/{item.total}
                </Text>
                <div style={{
                    width: '64px',
                    height: '6px',
                    backgroundColor: '#e1e1e1',
                    borderRadius: '999px',
                    overflow: 'hidden'
                }}>
                    <div
                        style={{
                            width: `${percentage}%`,
                            height: '100%',
                            backgroundColor: getColor(),
                            transition: 'all 0.5s ease'
                        }}
                    />
                </div>
            </div>
        );
    };

    // useEffect(() => {
    //
    // }, [location.pathname]);

    if (!auth?.isTalent()) {
        return null
    }

    return (
        <Modal
            okText={null}
            zIndex={199}
            footer={null}
            cancelText={null}
            title={null}
            open={shouldShowVersionUpdate}
            closeable={false}
            maskClosable={true} bodyStyle={{padding: 0}}
            style={{top: 20, maxWidth: '900px', width: '100%', padding:0}}
            destroyOnClose={true}
            width={'85%'}
            onCancel={() => {
                return setShouldShowVersionUpdate(false)
            }}
        >
            <div className="w-full max-w-[900px] mx-auto py-0">
                <Card>
                    <div className="mb-6">
                        <div className="mx-auto flex flex-row items-start mb-4 gap-3">
                            <BellOutlined className="mt-1" style={{fontSize: 25, color: TB.config('color.primary')}}/>
                            <Title level={3} className="mb-0">
                                Important: Your Wripple Profile Needs to be Updated
                            </Title>
                        </div>
                        <Paragraph>
                            Welcome to the latest Wripple update! In the spirit of continuous improvement and based upon
                            feedback from our clients, we have expanded the amount of data we are capturing as part
                            of your talent profile.
                        </Paragraph>
                    </div>

                    <div className="mb-6">
                        <Title level={5} className="mb-2">How does this affect you?</Title>
                        <Paragraph>
                            In order to apply for new jobs OR make any future updates to your profile,
                            you will need to complete some new, required data fields.
                        </Paragraph>
                    </div>

                    <div>
                        <Title level={5} className="flex flex-row items-center gap-2 mb-4">
                            <CheckCircleOutlined style={{color: TB.config('color.success')}}/>
                            New Required Fields:
                        </Title>

                        <div className="flex flex-col items-stretch gap-3.5">
                            {requirements.map((req, index) => (
                                <Card
                                    className="border-[#dadada]"
                                    key={index}
                                    size="small"
                                    bodyStyle={{
                                        padding: '8px 12px',
                                        backgroundColor: '#fafafa',
                                    }}
                                >
                                    <div className="flex flex-row justify-start items-start gap-3">
                                        <div className="mt-0.5">{req.icon}</div>
                                        <div className="flex-grow flex flex-col items-start gap-1">
                                            <Text strong className="text-sm">{req.title}</Text>
                                            <Text type="secondary" className="text-xs">{req.description}</Text>
                                        </div>
                                        <StatusIndicator item={req}/>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>

                    <Divider className="mt-5 mb-4"/>
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <QuestionCircleOutlined className="text-info text-sm"/>
                        <Text strong>
                            Thank you, and please reach out if you have questions or need help!
                        </Text>
                    </div>

                    <div className="flex flex-col items-center">

                        <Link to={'/talent/application'}>
                            <Button
                                type="primary"
                                size="large">
                                Continue to Profile
                            </Button>
                        </Link>
                    </div>
                </Card>
            </div>
        </Modal>
    );
};

export default TalentVersionUpdate2025;